import React, { useContext, useState } from 'react';
import { View } from 'react-native';
import { Card, Text, TextInput } from 'react-native-paper';
import { FormControl } from 'native-base';
import { ScrollView } from 'react-native-gesture-handler';
import { useMutation } from '@apollo/client';
import { DatePickerInput } from 'react-native-paper-dates';
import { Formik } from 'formik';
import Picker from '../../../components/commons/Picker';
import { styles } from '../../Common.style';
import { UpdateProfileMutation } from '../../../gql/profile/mutation';
import { FindOneUserQuery } from '../../../gql/user/query';
import { phoneSchema } from '../../../utils/yupSchema';
import {
  GENDER_TYPE, GRADE, LEGAL_GUARDIAN_TYPE, ROLE_NAMES,
} from '../../../utils/constant';
import { ISOCODE } from '../../../utils/isocode';
import AutocompleteLocation from '../../../components/commons/AutocompleteLocation';
import { computeAge } from '../../../utils/utils';
import { AuthContext } from '../../../context/AuthContext';
import { UpdateOneUser } from '../../../gql/user/mutation';

// Z-index values for Picker components - higher values for pickers that appear first
const zIndexGender = 5000;
const zIndexNationality = 4000;
const zIndexGrade = 3000;
const zIndexRlType = 2000;

function InfoTab({ user, disabled = false }) {
  const { authState } = useContext(AuthContext);
  const [openPicker, setOpenPicker] = useState(null);
  const [
    updateProfileForUser,
  ] = useMutation(UpdateProfileMutation);
  const [updateOneUser] = useMutation(UpdateOneUser);
  const saveProfileCallback = async (data) => {
    if (data) {
      // upload asset using gql
      if (authState.role === ROLE_NAMES.SUPERADMIN) {
        await updateOneUser({
          variables: {
            updateOneUserId: user.id,
            email: data.email,
            lastName: data.lastName,
            firstName: data.firstName,
            profile: {
              address: data.address,
              phone: data.phone,
              dateOfBirth: data.dateOfBirth,
              license: data.license,
              grade: data.grade,
              locationOfBirth: data.locationOfBirth,
              bio: data.bio,
              gender: data.gender,
              nationality: data.nationality,
              rlType: data.rlType,
              rlFirstName: data.rlFirstName,
              rlLastName: data.rlLastName,
              rlEmail: data.rlEmail,
              rlAddress: data.rlAddress,
              rlPhone: data.rlPhone,
            }
          },
          refetchQueries: [
            { query: FindOneUserQuery, variables: { email: user.email } },
          ],
        });
      } else {
        await updateProfileForUser({
          variables: {
            address: data.address,
            phone: data.phone,
            dateOfBirth: data.dateOfBirth,
            license: data.license,
            grade: data.grade,
            locationOfBirth: data.locationOfBirth,
            bio: data.bio,
            gender: data.gender,
            nationality: data.nationality,
            rlType: data.rlType,
            rlFirstName: data.rlFirstName,
            rlLastName: data.rlLastName,
            rlEmail: data.rlEmail,
            rlAddress: data.rlAddress,
            rlPhone: data.rlPhone,
          },
          refetchQueries: [
            { query: FindOneUserQuery, variables: { email: user.email } },
          ],
        });
      }
    }
  };

  return (
    <ScrollView keyboardShouldPersistTaps="always">
      <Card.Content>
        <Formik
          initialValues={{
            lastName: user.lastName,
            firstName: user.firstName,
            email: user.email,
            address: user.profile?.address,
            phone: user.profile?.phone,
            dateOfBirth: user.profile?.dateOfBirth,
            license: user.profile?.license,
            grade: user.profile?.grade,
            locationOfBirth: user.profile?.locationOfBirth,
            gender: user.profile?.gender,
            nationality: user.profile?.nationality,
            rlType: user.profile?.rlType,
            rlFirstName: user.profile?.rlFirstName,
            rlLastName: user.profile?.rlLastName,
            rlEmail: user.profile?.rlEmail,
            rlAddress: user.profile?.rlAddress,
            rlPhone: user.profile?.rlPhone,
          }}
          validateOnChange
          validationSchema={phoneSchema}
        >
          {({
            handleBlur, values, errors, setFieldValue,
          }) => (
            <>
              <FormControl style={styles.field}>
                <TextInput
                  label="Nom"
                  disabled={disabled}
                  onChangeText={(value) => setFieldValue('lastName', value)}
                  onBlur={() => saveProfileCallback({ lastName: values.lastName })}
                  left={<TextInput.Icon icon="account" />}
                  value={values.lastName}
                  style={styles.whiteBackground}
                />
              </FormControl>

              <FormControl style={styles.field}>
                <TextInput
                  label="Prénom"
                  disabled={disabled}
                  onChangeText={(value) => setFieldValue('firstName', value)}
                  onBlur={() => saveProfileCallback({ firstName: values.firstName })}
                  left={<TextInput.Icon icon="account" />}
                  value={values.firstName}
                  style={styles.whiteBackground}
                />
              </FormControl>

              <FormControl style={styles.field}>
                <TextInput
                  label="Email"
                  disabled={disabled}
                  onChangeText={(value) => setFieldValue('email', value)}
                  onBlur={() => saveProfileCallback({ email: values.email })}
                  left={<TextInput.Icon icon="email" />}
                  value={values.email}
                  style={styles.whiteBackground}
                />
              </FormControl>

              <View style={{ zIndex: zIndexGender }}>
                <FormControl style={styles.field}>
                  <FormControl.Label>Genre</FormControl.Label>
                  <Picker
                    style={styles.picker}
                    selectedValue={values.gender}
                    open={openPicker === 'gender'}
                    setOpen={(open) => setOpenPicker(open ? 'gender' : null)}
                    zIndex={zIndexGender}
                    zIndexInverse={zIndexRlType}
                    onValueChange={(value) => { saveProfileCallback({ gender: value }); }}
                  >
                    {Object.entries(GENDER_TYPE)
                      .map((k) => <Picker.Item key={k[0]} label={k[1]} value={k[0]} />)}
                  </Picker>
                </FormControl>
              </View>

              <View style={{ zIndex: zIndexNationality }}>
                <FormControl style={styles.field}>
                  <FormControl.Label>Nationalité</FormControl.Label>
                  <Picker
                    style={styles.picker}
                    selectedValue={values.nationality}
                    open={openPicker === 'nationality'}
                    setOpen={(open) => setOpenPicker(open ? 'nationality' : null)}
                    zIndex={zIndexNationality}
                    zIndexInverse={zIndexGender}
                    onValueChange={(value) => { saveProfileCallback({ nationality: value }); }}
                  >
                    {Object.entries(ISOCODE)
                      .sort((a, b) => a[1].localeCompare(b[1], 'en'))
                      .map((k) => <Picker.Item key={k[0]} label={k[1]} value={k[1]} />)}
                  </Picker>
                </FormControl>
              </View>

              <FormControl style={styles.field}>
                <FormControl.Label>Adresse</FormControl.Label>
                <AutocompleteLocation
                  value={values.address}
                  handleChangeText={(value) => setFieldValue('address', value)}
                  handleSubmit={(value) => {
                    setFieldValue('address', value);
                    saveProfileCallback(
                      { address: value },
                    );
                  }}
                />
              </FormControl>

              <FormControl style={styles.field}>
                <TextInput
                  label="Téléphone"
                  left={<TextInput.Icon icon="phone" />}
                  onChangeText={(value) => setFieldValue('phone', value)}
                  onBlur={() => {
                    handleBlur('phone');
                    if (!errors.phone && values.phone !== '') saveProfileCallback({ phone: values.phone });
                  }}
                  value={values.phone}
                  keyboardType="phone-pad"
                  style={styles.whiteBackground}
                />
                {!!errors.phone && <Text style={styles.error}>{errors.phone}</Text>}
              </FormControl>

              <FormControl style={styles.field}>
                <FormControl.Label>Date de naissance</FormControl.Label>
                <DatePickerInput
                  presentationStyle='pageSheet'
                  style={styles.whiteBackground}
                  locale="fr-FR"
                  label="Date de naissance"
                  value={new Date(values.dateOfBirth ?? Date.now())}
                  onChange={(d) => {
                    d.setHours(d.getHours() + 12);
                    if (d !== new Date(values.dateOfBirth)) saveProfileCallback({ dateOfBirth: d });
                  }}
                  inputMode="start"
                  mode="flat"
                />
              </FormControl>

              <FormControl style={styles.field}>
                <TextInput
                  label="Lieu de naissance"
                  onChangeText={(value) => setFieldValue('locationOfBirth', value)}
                  onBlur={() => saveProfileCallback({ locationOfBirth: values.locationOfBirth })}
                  left={<TextInput.Icon icon="map-marker" />}
                  value={values.locationOfBirth}
                  style={styles.whiteBackground}
                />
              </FormControl>

              <View style={{ zIndex: zIndexGrade }}>
                <FormControl style={styles.field}>
                  <FormControl.Label>Grade</FormControl.Label>
                  <Picker
                    style={styles.picker}
                    selectedValue={values.grade}
                    disabled={disabled}
                    open={openPicker === 'grade'}
                    setOpen={(open) => setOpenPicker(open ? 'grade' : null)}
                    zIndex={zIndexGrade}
                    zIndexInverse={zIndexNationality}
                    onValueChange={(value) => { saveProfileCallback({ grade: value }); }}
                  >
                    {Object.entries(GRADE)
                      .map((k) => <Picker.Item key={k[0]} label={k[1]} value={k[0]} />)}
                  </Picker>
                </FormControl>
              </View>

              <FormControl style={styles.field}>
                <TextInput
                  label="Numéro licence sportive"
                  onChangeText={(value) => setFieldValue('license', value)}
                  onBlur={() => saveProfileCallback({ license: values.license })}
                  left={<TextInput.Icon icon="identifier" />}
                  value={values.license}
                  style={styles.whiteBackground}
                />
              </FormControl>

              {computeAge(values.dateOfBirth) < 18 ? (
                <>
                  <View style={{ zIndex: zIndexRlType }}>
                    <FormControl style={styles.field}>
                      <FormControl.Label>Responsable légal</FormControl.Label>
                      <Picker
                        style={styles.picker}
                        selectedValue={values.rlType}
                        open={openPicker === 'rlType'}
                        setOpen={(open) => setOpenPicker(open ? 'rlType' : null)}
                        zIndex={zIndexRlType}
                        zIndexInverse={zIndexGrade}
                        onValueChange={(value) => { saveProfileCallback({ rlType: value }); }}
                      >
                        {Object.entries(LEGAL_GUARDIAN_TYPE)
                          .map((k) => <Picker.Item key={k[0]} label={k[1]} value={k[0]} />)}
                      </Picker>
                    </FormControl>
                  </View>
                  <FormControl style={styles.field}>
                    <TextInput
                      label="Nom du responsable légal"
                      onChangeText={(value) => setFieldValue('rlLastName', value)}
                      onBlur={() => saveProfileCallback({ rlLastName: values.rlLastName })}
                      left={<TextInput.Icon icon="account-child" />}
                      value={values.rlLastName}
                      style={styles.whiteBackground}
                    />
                  </FormControl>
                  <FormControl style={styles.field}>
                    <TextInput
                      label="Prénom du responsable légal"
                      onChangeText={(value) => setFieldValue('rlFirstName', value)}
                      onBlur={() => saveProfileCallback({ rlFirstName: values.rlFirstName })}
                      left={<TextInput.Icon icon="account-child" />}
                      value={values.rlFirstName}
                      style={styles.whiteBackground}
                    />
                  </FormControl>
                  <FormControl style={styles.field}>
                    <FormControl.Label>Adresse</FormControl.Label>
                    <AutocompleteLocation
                      value={values.rlAddress}
                      handleChangeText={(value) => setFieldValue('rlAddress', value)}
                      handleSubmit={(value) => {
                        setFieldValue('rlAddress', value);
                        saveProfileCallback(
                          { rlAddress: value },
                        );
                      }}
                    />
                  </FormControl>
                  <FormControl style={styles.field}>
                    <TextInput
                      label="Téléphone du responsable légal"
                      left={<TextInput.Icon icon="phone" />}
                      onChangeText={(value) => setFieldValue('rlPhone', value)}
                      onBlur={() => {
                        handleBlur('rlPhone');
                        if (!errors.rlPhone && values.rlPhone !== '') saveProfileCallback({ rlPhone: values.rlPhone });
                      }}
                      value={values.rlPhone}
                      keyboardType="phone-pad"
                      style={styles.whiteBackground}
                    />
                    {!!errors.rlPhone && <Text style={styles.error}>{errors.rlPhone}</Text>}
                  </FormControl>
                  <FormControl style={styles.field}>
                    <TextInput
                      label="Email du responsable légal"
                      onChangeText={(value) => setFieldValue('rlEmail', value)}
                      onBlur={() => saveProfileCallback({ rlEmail: values.rlEmail })}
                      left={<TextInput.Icon icon="email" />}
                      value={values.rlEmail}
                      style={styles.whiteBackground}
                    />
                  </FormControl>
                </>
              )
                : null}
            </>
          )}
        </Formik>
      </Card.Content>
    </ScrollView>
  );
}

export default InfoTab;
